// Quantities carry decimals; sums like 2.3 - 0.3 come out a hair off in
// floating point, which would refuse an exact minimum or save 1.9999999999.
const QUANTITY_SCALE = 1e6;

/** `value` rounded to the precision a quantity carries. */
export function roundQuantity(value: number): number {
  return Math.round(value * QUANTITY_SCALE) / QUANTITY_SCALE;
}

/** One line a need-row quantity is spread over, in need order. */
export interface SpreadLine {
  suggestionId: string;
  /** What the engine calculated for the line: its share before overflow. */
  calculated: number;
  /** A stock line, ordered with the need's SO lines (after them). */
  stock: boolean;
}

export interface NeedSpread {
  quantities: Map<string, number>;
  /** What no line takes: with no stock line, it goes on a new one. */
  overflow: number;
}

/**
 * Splits a need row's typed total over its lines (GEN-2938). SO lines come
 * first, oldest requested date first, each up to its own calculated quantity;
 * the last stock line takes whatever they leave, so lowering the total takes
 * from stock first, then from the newest SO lines. With no stock line, what is
 * left above the SO lines' sum is the overflow, for a new stock line. Every
 * line keeps at least 1 (an order quantity of 0 is not saved), so a total
 * below the line count is refused with null.
 */
export function spreadNeedQuantity(
  lines: readonly SpreadLine[],
  total: number,
): NeedSpread | null {
  if (!Number.isFinite(total)) return null;
  let remaining = roundQuantity(total);
  if (lines.length === 0 || remaining < lines.length) return null;
  const quantities = new Map<string, number>();
  lines.forEach((line, index) => {
    const later = lines.length - 1 - index;
    const quantity =
      later === 0 && line.stock
        ? remaining
        : Math.max(1, Math.min(line.calculated, remaining - later));
    quantities.set(line.suggestionId, quantity);
    remaining = roundQuantity(remaining - quantity);
  });
  return { quantities, overflow: remaining };
}
